<template>
    <div class="sidebar-popup-container">
        <div
            :class="[
                'sidebar-popup-container__icon-wrapper',
                { '-is-active': popupOpened }
            ]"
            @click.stop="changePopupState"
            name="popup-icon-wrapper"
        >
            <Icon icon="GridFilled" />
        </div>
        <div class="sidebar-popup" ref="popupRef" v-if="popupOpened">
            <div class="sidebar-popup__profile-info">
                <img
                    v-if="avatar"
                    :src="avatar"
                    class="sidebar-popup__profile-info__photo"
                />
                <div v-else class="sidebar-popup__profile-info__letter">
                    {{ letter }}
                </div>
                <div class="sidebar-popup__profile-info__wrapper">
                    <div class="sidebar-popup__profile-info__name">
                        {{ user.name }}
                    </div>
                    <div class="sidebar-popup__profile-info__e-mail">
                        {{ user.email }}
                    </div>
                </div>
            </div>
            <div class="sidebar-popup__divider" />
            <div class="sidebar-popup__tabs">
                <div
                    v-for="tab in tabs"
                    :class="[
                        'sidebar-popup__tab',
                        { '-is-active': tab.name === activeSection },
                        { '-is-disable': !tab.isActive }
                    ]"
                    @click="goToSection(tab)"
                >
                    <div class="sidebar-popup__tab__left">
                        <div class="sidebar-popup__tab__icon-wrapper">
                            <Icon
                                :icon="
                                    tab.icon
                                        ? tab.icon
                                        : tab.name.split(' ').join('_')
                                "
                            />
                        </div>
                        <div class="sidebar-popup__tab__name">
                            {{ tab.title }}
                        </div>
                    </div>

                    <div
                        v-if="tab.name === activeSection"
                        class="sidebar-popup__tab__icon-check"
                    >
                        <Icon icon="Check" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { reactive, ref, onMounted, onUnmounted, computed } from 'vue';

import Icon from './Icon.vue';
import Toggle from './Toggle.vue';
const props = defineProps({
    activeSection: String,
    avatar: String,
    user: Object
});

const emit = defineEmits(['logout', 'toggleDarkMode']);

const popupOpened = ref(false);
const popupRef = ref(null);

const tabs = reactive([
    {
        name: 'futures',
        title: 'Futures',
        isActive: true,
        icon: 'Futures'
    },
    {
        name: 'fx',
        title: 'FX',
        isActive: true,
        icon: 'Fx'
    },
    { name: 'spot', title: 'Spot', isActive: false, icon: 'Spot' },
    {
        name: 'trading terminal',
        title: 'Trading Terminal',
        isActive: false,
        icon: 'Terminal'
    }
]);

const letter = computed(() =>
    props.user && props.user.name ? props.user.name.charAt(0) : 'U'
);

const changePopupState = () => {
    popupOpened.value = !popupOpened.value;
};

const handleClickOutside = (event) => {
    if (popupRef.value && !popupRef.value.contains(event.target)) {
        changePopupState();
    }
};

const goToSection = (item) => {
    if (item.isActive && window !== undefined) {
        window.open(
            `https://${item.name}${
                window.location.hostname.includes('stage') ? '-stage' : ''
            }.scope360.io/`
        );
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>
<style lang="scss" scoped>
@import '../assets/styles/_main.scss';

.sidebar-popup-container {
    position: relative;

    &__icon-wrapper {
        width: 32px;
        height: 32px;
        display: flex;
        justify-content: center;
        align-items: center;
        color: $icons-mid-emphasis;
        cursor: pointer;

        &.-is-active {
            color: $icons-high-emphasis;
        }
    }
}

.sidebar-popup {
    position: absolute;
    top: 36px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px;
    width: 250px;
    border-radius: 12px;
    border: 1px solid $border;
    background: $card-background;
    @include shadow-medium;
    z-index: 20;

    &__profile-info {
        display: flex;
        align-items: center;
        gap: 12px;

        &__photo,
        &__letter {
            width: 32px;
            height: 32px;
            min-width: 32px;
            min-height: 32px;
            border-radius: 50%;
        }

        &__photo {
            object-fit: cover;
        }

        &__letter {
            @include fz-14;
            font-weight: 600;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: $card-primary-background-light;
            color: $primary-accent;
        }

        &__wrapper {
            display: flex;
            flex-direction: column;
            overflow: hidden;
            @include fz-14;
        }

        &__name {
            font-weight: 600;
            color: $text-high-emphasis;
        }

        &__e-mail {
            font-weight: 500;
            color: $text-mid-emphasis;
            text-overflow: ellipsis;
            overflow: hidden;
        }
    }

    &__divider {
        height: 1px;
        width: 100%;
        margin-right: 8px;
        background-color: $border;
    }

    &__tabs {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    &__tab {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 8px;
        border-radius: 10px;

        cursor: pointer;

        &__left {
            display: flex;
            gap: 8px;
            align-items: center;
        }

        &__icon-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
        }

        &__name {
            @include fz-14;
            color: $text-high-emphasis;
            font-weight: 600;
            font-variant-numeric: tabular-nums;
        }

        &__icon-check {
            display: flex;
            align-items: center;
            justify-content: center;
            color: $primary-accent;
        }

        &:hover:not(.-is-disable) {
            background-color: $card-neutral-background;
        }

        &.-is-disable {
            .sidebar-popup__tab__icon-wrapper {
                opacity: 0.3;
            }

            .sidebar-popup__tab__name {
                color: $text-low-emphasis;
            }
        }
    }
}
</style>
